/**
 * ECKEN DER GRUNDFORMEN IM SKALIERTEN RAUM (Kundenwunsch über Jan,
 * 26.10.2026: „Kann man den Eckradius so einstellen, dass er bei einer
 * ungleichmäßigen Skalierung gleichmäßig bleibt?“ — dazu Jan: „… wenn der
 * Radius zusätzlich trotz Vergrößerung der Fläche bleibt“ und „bei
 * ungleichmäßigem Skalieren und Keyframes geht es nicht?“).
 *
 * Die Rundung wird im Raum der Ebenen-Skalierung gebildet und zurück-
 * gerechnet: nach der Skalierung ist sie rund. Gemeinsam genutzt von der
 * SVG-Erzeugung (app/src/lib/grundformen) und — für animierte Skalierung —
 * pro Frame vom Renderer (grundformEcken, HTML-Vektoren): die SVG trägt
 * dafür die Eckpunkte als data-ecken-Attribute.
 */

export type EckenPunkt = [number, number];
export type EckenModus = "gleich" | "fest";

const zahl = (n: number) => String(Math.round(n * 100) / 100);

/**
 * Raum der Rundung: „gleich“ = Seitenverhältnis der Skalierung (kleinere
 * Achse 1, die Rundung wächst mit ihr); „fest“ = die Skalierung selbst
 * (Radius wie bei Skalierung 1); sonst [1, 1].
 */
export function eckenRaumAus(modus: EckenModus | null | undefined, sx: number, sy: number): [number, number] {
    const x = Math.abs(sx) || 1;
    const y = Math.abs(sy) || 1;
    if (modus === "fest") return [x, y];
    if (modus === "gleich") {
        const m = Math.min(x, y);
        return [x / m, y / m];
    }
    return [1, 1];
}

/** Vieleck mit gerundeten Ecken (radius gekappt je Kante), gerundet in `raum`. */
export function vieleckEckenPfad(eingabe: EckenPunkt[], radius: number, raum: [number, number] = [1, 1]): string {
    const [vx, vy] = raum;
    const punkte = eingabe.map(([x, y]): EckenPunkt => [x * vx, y * vy]);
    const p = (q: EckenPunkt) => `${zahl(q[0] / vx)} ${zahl(q[1] / vy)}`;
    const n = punkte.length;
    if (radius <= 0) return "M" + punkte.map(p).join("L") + "Z";
    let d = "";
    for (let i = 0; i < n; i++) {
        const vor = punkte[(i - 1 + n) % n];
        const ecke = punkte[i];
        const nach = punkte[(i + 1) % n];
        const lv = Math.hypot(vor[0] - ecke[0], vor[1] - ecke[1]);
        const ln = Math.hypot(nach[0] - ecke[0], nach[1] - ecke[1]);
        const r = Math.min(radius, lv / 2, ln / 2);
        const a: EckenPunkt = [ecke[0] + ((vor[0] - ecke[0]) / lv) * r, ecke[1] + ((vor[1] - ecke[1]) / lv) * r];
        const b: EckenPunkt = [ecke[0] + ((nach[0] - ecke[0]) / ln) * r, ecke[1] + ((nach[1] - ecke[1]) / ln) * r];
        d += `${i === 0 ? "M" : "L"}${p(a)}Q${p(ecke)} ${p(b)}`;
    }
    return d + "Z";
}

/**
 * Füll-Ringe des Sternvielecks {n/k} (s. app/src/lib/grundformen
 * sternFuellRinge); Ring 1 mit runden Spitzen genau wie die Linien.
 */
export function sternRingePfad(
    eingabe: EckenPunkt[][],
    k: number,
    radius: number,
    raum: [number, number] = [1, 1],
): string {
    const [vx, vy] = raum;
    const ringe = eingabe.map((ring) => ring.map(([x, y]): EckenPunkt => [x * vx, y * vy]));
    const p = (q: EckenPunkt) => `${zahl(q[0] / vx)} ${zahl(q[1] / vy)}`;
    const abst = (a: EckenPunkt, b: EckenPunkt) => Math.hypot(a[0] - b[0], a[1] - b[1]);
    return ringe
        .map((ring, w) => {
            if (w > 0 || radius <= 0) return "M" + ring.map(p).join("L") + "Z";
            const n = ring.length / 2;
            let d = "";
            for (let i = 0; i < n; i++) {
                const spitze = ring[2 * i];
                const vor = ring[(2 * i - 1 + 2 * n) % (2 * n)];
                const nach = ring[2 * i + 1];
                // Nachbarn im Linienzug (wie vieleckEckenPfad sie rundet): Spitzen i ∓ k
                const sv = ring[2 * ((i - k + n) % n)];
                const sn = ring[2 * ((i + k) % n)];
                const lv = abst(spitze, sv);
                const ln = abst(spitze, sn);
                const r = Math.min(radius, lv / 2, ln / 2, abst(spitze, vor), abst(spitze, nach));
                const a: EckenPunkt = [
                    spitze[0] + ((sv[0] - spitze[0]) / lv) * r,
                    spitze[1] + ((sv[1] - spitze[1]) / lv) * r,
                ];
                const b: EckenPunkt = [
                    spitze[0] + ((sn[0] - spitze[0]) / ln) * r,
                    spitze[1] + ((sn[1] - spitze[1]) / ln) * r,
                ];
                d += `${i === 0 ? "M" : "L"}${p(a)}Q${p(spitze)} ${p(b)}L${p(nach)}`;
            }
            return d + "Z";
        })
        .join("");
}

/** rx/ry eines Rechtecks: Rundung im Raum, dort je Kante gekappt. */
export function rechteckEcken(radius: number, breite: number, hoehe: number, raum: [number, number]): [number, number] {
    const [vx, vy] = raum;
    const r = Math.min(radius, (breite * vx) / 2, (hoehe * vy) / 2);
    return [r / vx, r / vy];
}

/**
 * PRO FRAME (HTML-Vektor): Ecken für die AKTUELLE Skalierung der Ebene neu
 * bilden — so bleiben sie auch bei per Keyframes animierter Skalierung
 * rund. Schreibt nur, wenn sich der Raum geändert hat.
 */
export function grundformEcken(wurzel: Element, sx: number, sy: number): void {
    const svg = wurzel.matches("[data-ecken-modus]") ? wurzel : wurzel.querySelector("[data-ecken-modus]");
    if (!svg) return;
    const modus = svg.getAttribute("data-ecken-modus") as EckenModus | null;
    const raum = eckenRaumAus(modus, sx, sy);
    const stand = `${raum[0].toFixed(4)}:${raum[1].toFixed(4)}`;
    if (svg.getAttribute("data-ecken-stand") === stand) return;
    svg.setAttribute("data-ecken-stand", stand);
    svg.querySelectorAll("[data-ecken]").forEach((el) => {
        const art = el.getAttribute("data-ecken");
        const radius = Number(el.getAttribute("data-r")) || 0;
        try {
            if (art === "rect") {
                const [rx, ry] = rechteckEcken(
                    radius,
                    Number(el.getAttribute("width")) || 0,
                    Number(el.getAttribute("height")) || 0,
                    raum,
                );
                el.setAttribute("rx", zahl(rx));
                el.setAttribute("ry", zahl(ry));
            } else if (art === "vieleck") {
                const teile = JSON.parse(el.getAttribute("data-punkte") || "[]") as EckenPunkt[][];
                el.setAttribute("d", teile.map((t) => vieleckEckenPfad(t, radius, raum)).join(""));
            } else if (art === "stern") {
                const ringe = JSON.parse(el.getAttribute("data-punkte") || "[]") as EckenPunkt[][];
                const k = Number(el.getAttribute("data-k")) || 2;
                el.setAttribute("d", sternRingePfad(ringe, k, radius, raum));
            }
        } catch {
            /* kaputte Daten: Form bleibt, wie sie erzeugt wurde */
        }
    });
}
